import { Suspense } from 'react';
import LazyLoad from 'react-lazyload';

import {
    ProductAttributes,
    ProductGallery as SourceProductGallery,
    ProductInformation,
    ProductPage as SourceProductPage,
    ProductReviews,
} from 'AdvoxRoute/ProductPage/ProductPage.component';
import AutoRelatedSlider from 'Component/AutoRelatedSlider';
import CmsBlock from 'Component/CmsBlock';
import CmsBlockProvider from 'Component/CmsBlockProvider';
import ContentWrapper from 'Component/ContentWrapper';
import Delayed from 'Component/Delayed';
import Loader from 'Component/Loader/Loader.component';
import PaymentDeadline from 'Component/PaymentDeadline';
import ProductActions from 'Component/ProductActions';
import ProductAttachments from 'Component/ProductAttachments';
import ProductLinks from 'Component/ProductLinks';
import { GROUP_01, GROUP_02 } from 'Component/ProductTabs/ProductTabs.config';
import ProductTags from 'Component/ProductTags';
import PromoBanner from 'Component/PromoBanner';
import WPEvents from 'Component/WPEvents';
import NoMatchHandler from 'Route/NoMatchHandler';
import {
    PAYMENT_DEADLINE,
    PRODUCT_APPLICABILITY,
    PRODUCT_ATTACHMENTS,
    PRODUCT_ATTRIBUTES,
    PRODUCT_INFORMATION,
    PRODUCT_REVIEWS,
    RECOMMENDED_CATEGORIES,
    STATIC_SECTION_CMS_BLOCK_ID,
} from 'Route/ProductPage/ProductPage.config';
import { RELATED, UPSELL } from 'Store/LinkedProducts/LinkedProducts.reducer';

export { ProductInformation, ProductReviews, ProductAttributes };

export const ProductGallery = SourceProductGallery;
/** @namespace Pwa/Route/ProductPage/Component */
export class ProductPage extends SourceProductPage {
    tabMap = {
        [PRODUCT_INFORMATION]: {
            tabKey: 'ProductInformation',
            name: __('Description'),
            group: GROUP_01,
            shouldTabRender: () => {
                const { isInformationTabEmpty } = this.props;
                return !isInformationTabEmpty;
            },
            render: (key) => this.renderProductInformationTab(key),
        },
        [PRODUCT_APPLICABILITY]: {
            tabKey: 'ProductTags',
            name: __('Applicability'),
            group: GROUP_01,
            shouldTabRender: () => {
                const { isProductTagsTabEmpty } = this.props;
                return !isProductTagsTabEmpty;
            },
            render: (key) => this.renderProductTags(key),
        },
        [PRODUCT_ATTRIBUTES]: {
            tabKey: 'ProductAttributes',
            name: __('Technical details'),
            group: GROUP_01,
            shouldTabRender: () => {
                const { isAttributesTabEmpty } = this.props;

                return !isAttributesTabEmpty;
            },
            render: (key) => this.renderProductAttributesTab(key),
        },

        [PRODUCT_REVIEWS]: {
            tabKey: 'ProductReviews',
            name: __('Reviews'),
            group: GROUP_01,
            // Return true since we always show 'Add review' button
            shouldTabRender: () => true,
            render: (key) => this.renderProductReviewsTab(key),
        },
        [PRODUCT_ATTACHMENTS]: {
            tabKey: 'ProductAttachments',
            name: __('Product attachments'),
            group: GROUP_01,
            shouldTabRender: () => {
                const { isProductAttachmentTabEmpty } = this.props;
                return !isProductAttachmentTabEmpty;
            },
            render: (key) => this.renderProductAttachments(key),
        },
        [PAYMENT_DEADLINE]: {
            tabKey: 'PaymentDeadline',
            name: __('Shipping and payment'),
            group: GROUP_01,
            shouldTabRender: () => true,
            render: (key) => this.renderPaymentDeadlineTab(key),
        },
    };

    componentDidUpdate(prevProps) {
        const { requestProductTags } = this.props;
        const { activeProduct: prevActiveProduct } = prevProps || {};
        const { activeProduct } = this.props || {};

        if (activeProduct && prevActiveProduct && activeProduct.id !== prevActiveProduct.id && activeProduct.id) {
            requestProductTags(activeProduct);
        }
    }

    renderStaticSection() {
        return (
            <>
                <CmsBlock identifier={STATIC_SECTION_CMS_BLOCK_ID} />
                <CmsBlock identifier={RECOMMENDED_CATEGORIES} />
            </>
        );
    }

    renderProductAttachments(key) {
        const {
            activeProduct: { sku },
        } = this.props;

        if (!sku) return null;

        return (
            <Suspense fallback={<Loader />} key={key}>
                <ProductAttachments productSku={sku} name={key} />
            </Suspense>
        );
    }

    renderPaymentDeadlineTab(key) {
        const {
            activeProduct: { sku },
        } = this.props;

        if (!sku) return null;

        return (
            <Suspense fallback={<Loader />} key={key}>
                <PaymentDeadline name={key} />
            </Suspense>
        );
    }

    renderProductTags(key) {
        const {
            activeProduct: { id },
        } = this.props;

        return id && id > 0 ? (
            <Suspense fallback={<Loader />} key={key}>
                <ProductTags productId={id} name={key} />
            </Suspense>
        ) : null;
    }

    renderProductAttributesTab(key) {
        const { activeProduct, areDetailsLoaded } = this.props;

        return (
            <Suspense fallback={<Loader />} key={key}>
                <ProductAttributes product={activeProduct} areDetailsLoaded={areDetailsLoaded} key={key} name={key} />
            </Suspense>
        );
    }

    shouldTabsRender() {
        return Object.entries(this.tabMap)
            .map(([id, values]) => ({ id, ...values }))
            .filter(({ shouldTabRender }) => shouldTabRender());
    }

    renderAutoRelatedSection(position) {
        const {
            activeProduct: { uid },
        } = this.props;

        if (!uid) {
            return null;
        }

        return (
            <LazyLoad once offset={100}>
                <AutoRelatedSlider type="mpARPProductPage" position={position} uid={uid} />
            </LazyLoad>
        );
    }

    renderRelatedProducts() {
        const { areDetailsLoaded } = this.props;

        return (
            <LazyLoad once offset={100}>
                <ProductLinks
                    linkType={RELATED}
                    title={__('Recommended for you')}
                    areDetailsLoaded={areDetailsLoaded}
                />
            </LazyLoad>
        );
    }

    renderUpsellProducts() {
        const { areDetailsLoaded } = this.props;

        return (
            <LazyLoad once offset={100}>
                <ProductLinks linkType={UPSELL} title={__('You might also like')} areDetailsLoaded={areDetailsLoaded} />
            </LazyLoad>
        );
    }

    renderProductPageContent() {
        const {
            getLink,
            dataSource,
            areDetailsLoaded,
            activeProduct,
            setActiveProduct,
            useEmptyGallerySwitcher,
            parameters,
            isVariant,
            showAlternativeDesign,
        } = this.props;

        return (
            <>
                {this.renderNameMobile()}
                <Suspense fallback={<Loader />}>
                    <ProductGallery
                        product={activeProduct}
                        areDetailsLoaded={areDetailsLoaded}
                        isWithEmptySwitcher={useEmptyGallerySwitcher}
                        showLoader={isVariant}
                        showAlternativeDesign={showAlternativeDesign}
                    />
                </Suspense>
                <Delayed>
                    <ProductActions
                        getLink={getLink}
                        product={dataSource}
                        parameters={parameters}
                        areDetailsLoaded={areDetailsLoaded}
                        setActiveProduct={setActiveProduct}
                        showAlternativeDesign={showAlternativeDesign}
                    />
                </Delayed>
            </>
        );
    }

    renderProductWPEvents() {
        const { activeProduct, activeProduct: { id = '' } = {} } = this.props;

        if (!id) {
            return null;
        }

        return <WPEvents eventType="track" eventKey="ViewContent" contentName="ViewProduct" data={activeProduct} />;
    }

    renderBanner(elem, section) {
        const {
            activeProduct: { id },
        } = this.props;

        return id ? (
            <div block="ProductPage" elem={`${elem}Banner`}>
                <PromoBanner section={section} productId={id} />
            </div>
        ) : null;
    }

    render() {
        return (
            <NoMatchHandler>
                <main block="ProductPage" aria-label="Product page" itemScope itemType="http://schema.org/Product">
                    {this.renderProductWPEvents()}
                    {this.renderBanner('Top', 'productTop')}
                    <CmsBlockProvider identifiers={['info_tiles', 'promo_banners_PDP', 'payment_deadline_pdp']}>
                        <ContentWrapper
                            wrapperMix={{ block: 'ProductPage', elem: 'Wrapper' }}
                            label={__('Main product details')}
                        >
                            {this.renderProductPageContent()}
                        </ContentWrapper>
                        <Delayed>
                            <section block="ProductPage" elem="ContentWrapper">
                                {this.renderUpsellProducts()}
                                {this.renderBanner('PDPSM', 'productSM')}
                                {this.renderProductTabs(true, GROUP_01)}
                                {this.renderProductTabs(false, GROUP_02)}
                                {this.renderAutoRelatedSection('product_content_bottom')}
                                {this.renderStaticSection()}
                            </section>
                        </Delayed>
                    </CmsBlockProvider>
                </main>
            </NoMatchHandler>
        );
    }
}

export default ProductPage;
